/**
 * AdminLayout — the persistent chrome (shell) wrapping every authenticated admin
 * screen. It renders the left navigation drawer (permission-filtered, grouped into
 * sections), a sticky top AppBar (global search field, theme toggle, language menu,
 * user avatar), and a content region into which the routed page is rendered as
 * `children`. Also hosts the Change Password dialog.
 *
 * Bilingual/RTL aware: in Arabic the whole document is set to `dir="rtl"` (in
 * main.tsx), so the flex row auto-reverses and the drawer moves to the inline-start
 * (right) on its own — no manual `order` swap (that caused a double-flip). The
 * drawer's `anchor` follows the language so its border sits on the inner edge.
 * Data loaded: open support-ticket count via React
 * Query (`['support-ticket-stats']`, polled every 60s) to drive the sidebar badge.
 * Main mutation: `changePassword` from the password dialog.
 */
import {
  Box, AppBar, Toolbar, Typography, IconButton, Avatar, Drawer, List, ListItem,
  ListItemButton, ListItemIcon, ListItemText, ListSubheader, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, Button, Alert, CircularProgress, Menu, MenuItem,
  InputAdornment, Chip,
} from '@mui/material';
import DashboardIcon from '@mui/icons-material/Dashboard';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import EventNoteIcon from '@mui/icons-material/EventNote';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import PeopleIcon from '@mui/icons-material/People';
import GroupsIcon from '@mui/icons-material/Groups';
import SportsIcon from '@mui/icons-material/Sports';
import LeaderboardIcon from '@mui/icons-material/Leaderboard';
import HistoryIcon from '@mui/icons-material/History';
import SportsSoccerIcon from '@mui/icons-material/SportsSoccer';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import BarChartIcon from '@mui/icons-material/BarChart';
import AssignmentIcon from '@mui/icons-material/Assignment';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import NotificationsIcon from '@mui/icons-material/Notifications';
import SettingsIcon from '@mui/icons-material/Settings';
import SchoolIcon from '@mui/icons-material/School';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import LockIcon from '@mui/icons-material/Lock';
import LogoutIcon from '@mui/icons-material/Logout';
import MenuIcon from '@mui/icons-material/Menu';
import LanguageIcon from '@mui/icons-material/Language';
import LightModeIcon from '@mui/icons-material/LightMode';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import DescriptionIcon from '@mui/icons-material/Description';
import SecurityIcon from '@mui/icons-material/Security';
import MapIcon from '@mui/icons-material/Map';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import SearchIcon from '@mui/icons-material/Search';
import StadiumIcon from '@mui/icons-material/Stadium';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import WebIcon from '@mui/icons-material/Web';
import { Fragment, ReactNode, useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { changePassword } from '../api/auth';
import { setLanguage, LANGUAGES, type LanguageCode } from '../i18n';
import { useAuth } from '../contexts/AuthContext';
import { COLORS, getColors } from '../theme';
import { useThemeMode } from '../contexts/ThemeModeContext';
import { PageTransition } from './PageTransition';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getTicketStats } from '../api/support-tickets';
import logoSvg from '../assets/logo.svg';

/** Compact 0–9999, capped as "9999+" to keep the badge a known width. */
function formatBadgeCount(n: number): string {
  if (n <= 0) return '';
  return n > 9999 ? '9999+' : String(n);
}

/** Fixed width (px) of the navigation drawer, shared by the desktop and mobile variants. */
const drawerWidth = 260;

/** One navigation link. `permission`, when set, gates visibility via {@link useAuth}().hasPermission. */
interface NavItem { path: string; label: string; icon: ReactNode; permission?: string; }

/** A titled group of nav links rendered under a `ListSubheader`. */
interface NavSection {
  /** i18n key, e.g. nav.dataView */
  titleKey: string;
  items: NavItem[];
}

interface AdminLayoutProps {
  /** The routed page content rendered in the main region. */
  children: ReactNode;
}

/**
 * Layout shell component. Reads auth/permissions, current route, language, and
 * theme mode from context; builds the permission-filtered nav sections; and renders
 * the drawer + AppBar + `children`. Owns local UI state for the mobile drawer, the
 * language menu, and the Change Password dialog form.
 */
export function AdminLayout({ children }: AdminLayoutProps) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { hasPermission, clearAuth, user } = useAuth();
  // A nav item is "active" when the URL exactly matches its path, or (for non-root
  // paths) when the URL is nested under it — so detail pages keep the parent highlighted.
  const isActive = (path: string) => location.pathname === path || (path !== '/' && location.pathname.startsWith(path));
  const isRtl = i18n.language === 'ar';
  const drawerAnchor = isRtl ? 'right' as const : 'left' as const;
  const { mode, toggle: toggleThemeMode } = useThemeMode();
  const c = getColors(mode);

  const queryClient = useQueryClient();

  // Sidebar badges — open ticket count is the only one we surface right now.
  // Polls every 60s; mutations elsewhere also invalidate the same query key.
  const { data: ticketStats } = useQuery({
    queryKey: ['support-ticket-stats'],
    queryFn: getTicketStats,
    refetchInterval: 60_000,
    staleTime: 30_000,
  });
  const openTicketBadge = formatBadgeCount(ticketStats?.open ?? 0);

  // SSE: real-time support-ticket updates — keeps sidebar badge + ticket list current.
  const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001/api/v1';
  const ticketSseRef = useRef<EventSource | null>(null);
  useEffect(() => {
    const token = localStorage.getItem('mstms_access_token');
    if (!token) return;

    const url = `${API_BASE_URL}/support-tickets/events?token=${encodeURIComponent(token)}`;
    const es = new EventSource(url);
    ticketSseRef.current = es;

    es.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        // Dispatch to any open dialog/component listening for ticket SSE events
        window.dispatchEvent(new CustomEvent('support-sse', { detail: data }));
        if (data.type === 'typing') return; // Don't refetch on typing events
        queryClient.invalidateQueries({ queryKey: ['support-ticket-stats'] });
        queryClient.invalidateQueries({ queryKey: ['support-tickets'] });
        if (data.ticketId) {
          queryClient.invalidateQueries({ queryKey: ['support-ticket', data.ticketId] });
        }
      } catch { /* ignore */ }
    };

    es.onerror = () => { /* EventSource auto-reconnects */ };

    return () => { es.close(); ticketSseRef.current = null; };
  }, [queryClient, API_BASE_URL]);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [langAnchor, setLangAnchor] = useState<null | HTMLElement>(null);
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [changePasswordLoading, setChangePasswordLoading] = useState(false);
  const [changePasswordError, setChangePasswordError] = useState<string | null>(null);
  const [changePasswordSuccess, setChangePasswordSuccess] = useState(false);

  const handleDrawerToggle = () => setMobileOpen(!mobileOpen);

  // Clear stored tokens/user and redirect to the login screen (replace = no back nav).
  const handleLogout = () => {
    clearAuth();
    navigate('/login', { replace: true });
  };

  // Open the Change Password dialog with all fields/flags reset.
  const handleChangePasswordOpen = () => {
    setChangePasswordOpen(true);
    setCurrentPassword(''); setNewPassword(''); setConfirmPassword('');
    setChangePasswordError(null); setChangePasswordSuccess(false);
  };

  // Validate locally (match + min length), call the changePassword API, then show a
  // success alert and auto-close the dialog; API errors surface in the error alert.
  const handleChangePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setChangePasswordError(null);
    if (newPassword !== confirmPassword) { setChangePasswordError(t('changePassword.passwordMismatch')); return; }
    if (newPassword.length < 8) { setChangePasswordError(t('changePassword.passwordTooShort')); return; }
    setChangePasswordLoading(true);
    try {
      await changePassword({ currentPassword, newPassword });
      setChangePasswordSuccess(true);
      setTimeout(() => setChangePasswordOpen(false), 1500);
    } catch (err: unknown) {
      const message = err && typeof err === 'object' && 'response' in err
        ? (err as { response?: { data?: { message?: string } } }).response?.data?.message : t('changePassword.failed');
      setChangePasswordError(message || t('changePassword.failed'));
    } finally { setChangePasswordLoading(false); }
  };

  // Switch app language (persists + toggles RTL via i18n) and close the language menu.
  const handleLangSelect = (code: LanguageCode) => { setLanguage(code); setLangAnchor(null); };

  const subheaderSx = {
    bgcolor: 'transparent',
    color: 'rgba(255,255,255,0.55)',
    fontSize: '0.72rem',
    fontWeight: 700,
    lineHeight: '32px',
    py: 0.5,
    px: 2,
  };

  /** Tasks / المهام — above Data view */
  const tasksNavItems: NavItem[] = [
    { path: '/tasks', label: t('nav.tasks'), icon: <TaskAltIcon />, permission: 'task_view' },
    { path: '/support', label: t('nav.support'), icon: <SupportAgentIcon />, permission: 'support_view' },
  ];

  /** Data view / بيانات البطولة */
  const dataViewNavItems: NavItem[] = [
    { path: '/', label: t('nav.dashboard'), icon: <DashboardIcon />, permission: 'dashboard_view' },
    { path: '/season-control', label: t('nav.seasonControl', { defaultValue: 'Season Control' }), icon: <EventNoteIcon />, permission: 'season_view' },
    { path: '/analytics', label: t('nav.analytics'), icon: <AnalyticsIcon />, permission: 'analytics_view' },
    { path: '/statistics', label: t('nav.statistics'), icon: <BarChartIcon />, permission: 'statistics_view' },
    { path: '/financial', label: t('nav.financial'), icon: <AccountBalanceIcon />, permission: 'financial_view' },
  ];

  const tournamentNavItems: NavItem[] = [
    { path: '/season-setup', label: t('nav.seasonSetup', { defaultValue: 'Season Setup' }), icon: <EventNoteIcon />, permission: 'season_view' },
    { path: '/seasons', label: t('nav.seasons'), icon: <CalendarMonthIcon />, permission: 'season_view' },
    { path: '/championships', label: t('nav.championshipView'), icon: <CalendarMonthIcon />, permission: 'season_view' },
    { path: '/tournaments', label: t('nav.tournaments'), icon: <EmojiEventsIcon />, permission: 'tournament_view' },
    { path: '/tournament-hierarchy', label: t('nav.hierarchy'), icon: <AccountTreeIcon />, permission: 'tournament_view' },
    { path: '/participants', label: t('nav.teamsGroups', { defaultValue: 'Teams & Groups' }), icon: <GroupsIcon />, permission: 'tournament_view' },
    { path: '/matches', label: t('nav.matches'), icon: <SportsIcon />, permission: 'match_view' },
    { path: '/scheduler', label: t('nav.scheduler'), icon: <EventNoteIcon />, permission: 'match_view' },
  ];

  const systemDataNavItems: NavItem[] = [
    { path: '/users', label: t('nav.users'), icon: <PeopleIcon />, permission: 'user_view' },
    { path: '/regions', label: t('nav.regionsOffices'), icon: <MapIcon />, permission: 'region_view' },
    { path: '/schools', label: t('nav.schools'), icon: <SchoolIcon />, permission: 'school_view' },
    { path: '/teams', label: t('nav.teams'), icon: <GroupsIcon />, permission: 'team_view' },
    { path: '/players', label: t('nav.players', { defaultValue: 'Players' }), icon: <SportsIcon />, permission: 'team_view' },
    { path: '/sports', label: t('nav.sports'), icon: <SportsSoccerIcon />, permission: 'sport_view' },
    { path: '/playgrounds', label: t('nav.playgrounds'), icon: <StadiumIcon />, permission: 'playground_view' },
    { path: '/banks', label: t('nav.banks', { defaultValue: 'Banks' }), icon: <AccountBalanceIcon />, permission: 'settings_view' },
  ];

  const adminNavItems: NavItem[] = [
    { path: '/standings', label: t('nav.standings'), icon: <LeaderboardIcon />, permission: 'standing_view' },
    { path: '/roles', label: t('nav.roles'), icon: <SecurityIcon />, permission: 'admin_role_view' },
    { path: '/invitations', label: t('nav.invitations', { defaultValue: 'Invitations' }), icon: <MailOutlineIcon />, permission: 'invitation_view' },
    { path: '/activity-logs', label: t('nav.activityLog'), icon: <HistoryIcon />, permission: 'activity_log_view' },
    { path: '/parental-consent', label: t('nav.parentalConsent'), icon: <AssignmentIcon />, permission: 'consent_view' },
    { path: '/notifications', label: t('nav.notifications'), icon: <NotificationsIcon />, permission: 'notification_view' },
    { path: '/website-content', label: t('nav.websiteContent'), icon: <WebIcon />, permission: 'settings_view' },
    { path: '/contract-templates', label: t('nav.contractTemplates', { defaultValue: 'Contract Templates' }), icon: <DescriptionIcon />, permission: 'settings_view' },
    { path: '/settings', label: t('nav.settings'), icon: <SettingsIcon />, permission: 'settings_view' },
  ];

  // Internal / developer-facing tooling — kept together as the LAST sidebar group.
  const internalDataNavItems: NavItem[] = [
    { path: '/schema', label: t('nav.dataModel', { defaultValue: 'Data Model' }), icon: <AccountTreeIcon /> },
    { path: '/api-docs', label: t('nav.apiDocs'), icon: <DescriptionIcon />, permission: 'api_docs_view' },
    { path: '/system-health', label: t('nav.systemHealth'), icon: <MonitorHeartIcon />, permission: 'settings_view' },
  ];

  const navSections: NavSection[] = [
    { titleKey: 'nav.tasks', items: tasksNavItems },
    { titleKey: 'nav.dataView', items: dataViewNavItems },
    { titleKey: 'nav.tournamentManagement', items: tournamentNavItems },
    { titleKey: 'nav.systemData', items: systemDataNavItems },
    { titleKey: 'nav.admin', items: adminNavItems },
    { titleKey: 'nav.internalData', items: internalDataNavItems },
  ];

  const drawer = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', bgcolor: c.sidebarBg, color: c.sidebarText }}>
      {/* Logo */}
      <Box sx={{ p: 2.5, display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Box component="img" src={logoSvg} alt="Dawri Madaris" sx={{ height: 40, width: 'auto' }} />
        <Box>
          <Typography variant="subtitle1" sx={{ color: '#fff', fontWeight: 700, lineHeight: 1.2, fontSize: '0.95rem' }}>
            {t('nav.appName')}
          </Typography>
          <Typography variant="caption" sx={{ color: c.sidebarText, fontSize: '0.7rem' }}>
            {t('nav.appSubtitle')}
          </Typography>
        </Box>
      </Box>

      {/* Nav Items */}
      <List sx={{ flexGrow: 1, overflow: 'auto', px: 1.5, '& .MuiListItemButton-root': { borderRadius: 2, mb: 0.3, px: 2, py: 1 } }}>
        {navSections.map((section, sectionIndex) => {
          // Hide links the user lacks permission for; skip the whole section if none remain.
          const visible = section.items.filter((item) => !item.permission || hasPermission(item.permission));
          if (visible.length === 0) return null;
          // Only add top margin above this section's header if some earlier section
          // actually rendered items (avoids a leading gap above the first visible group).
          const prevHadItems = navSections.slice(0, sectionIndex).some((s) =>
            s.items.some((i) => !i.permission || hasPermission(i.permission)),
          );
          return (
            <Fragment key={section.titleKey}>
              <ListSubheader
                component="div"
                disableSticky
                sx={{ ...subheaderSx, mt: prevHadItems ? 1 : 0 }}
              >
                {t(section.titleKey)}
              </ListSubheader>
              {visible.map((item) => {
                const badgeText = item.path === '/support' ? openTicketBadge : '';
                return (
                <ListItem key={item.path} disablePadding>
                  <ListItemButton
                    selected={isActive(item.path)}
                    onClick={() => { navigate(item.path); setMobileOpen(false); }}
                    sx={{
                      color: c.sidebarText,
                      '&.Mui-selected': { bgcolor: c.sidebarActive, color: '#fff', '& .MuiListItemIcon-root': { color: '#fff' } },
                      '&:hover': { bgcolor: 'rgba(255,255,255,0.08)' },
                    }}
                  >
                    <ListItemIcon sx={{ color: 'inherit', minWidth: 36, fontSize: 20 }}>{item.icon}</ListItemIcon>
                    <ListItemText primary={item.label} primaryTypographyProps={{ fontSize: '0.85rem', fontWeight: isActive(item.path) ? 600 : 400 }} />
                    {badgeText && (
                      <Box
                        component="span"
                        sx={{
                          ml: 1,
                          minWidth: 32,
                          height: 22,
                          px: 0.75,
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          borderRadius: 999,
                          bgcolor: c.error,
                          color: '#fff',
                          fontSize: '0.7rem',
                          fontWeight: 700,
                          letterSpacing: '0.02em',
                          lineHeight: 1,
                        }}
                      >
                        {badgeText}
                      </Box>
                    )}
                  </ListItemButton>
                </ListItem>
                );
              })}
            </Fragment>
          );
        })}
      </List>

      {/* Bottom actions */}
      <Box sx={{ p: 1.5 }}>
        <ListItemButton onClick={handleChangePasswordOpen} sx={{ borderRadius: 2, color: c.sidebarText, '&:hover': { bgcolor: 'rgba(255,255,255,0.08)' } }}>
          <ListItemIcon sx={{ color: 'inherit', minWidth: 36 }}><LockIcon fontSize="small" /></ListItemIcon>
          <ListItemText primary={t('nav.changePassword')} primaryTypographyProps={{ fontSize: '0.85rem' }} />
        </ListItemButton>
        <ListItemButton onClick={handleLogout} sx={{ borderRadius: 2, color: '#ff8a8a', '&:hover': { bgcolor: 'rgba(255,100,100,0.1)' } }}>
          <ListItemIcon sx={{ color: 'inherit', minWidth: 36 }}><LogoutIcon fontSize="small" /></ListItemIcon>
          <ListItemText primary={t('nav.logout')} primaryTypographyProps={{ fontSize: '0.85rem' }} />
        </ListItemButton>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: c.bgPage, width: '100%', maxWidth: '100vw' }}>
      {/* Desktop sidebar */}
      <Drawer
        variant="permanent"
        anchor={drawerAnchor}
        open
        sx={{
          display: { xs: 'none', md: 'block' },
          width: drawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            border: 'none',
            boxSizing: 'border-box',
            position: 'sticky',
            top: 0,
            height: '100vh',
          },
        }}
      >
        {drawer}
      </Drawer>

      <Drawer
        variant="temporary"
        anchor={drawerAnchor}
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{ keepMounted: true }}
        sx={{ display: { xs: 'block', md: 'none' }, '& .MuiDrawer-paper': { width: drawerWidth, border: 'none', boxSizing: 'border-box' } }}
      >
        {drawer}
      </Drawer>

      {/* Main Content */}
      <Box component="div" sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, overflow: 'hidden' }}>
        {/* Top Bar */}
        <AppBar position="sticky" elevation={0} sx={{ bgcolor: c.bgAppBar, color: c.textPrimary, borderBottom: `1px solid ${c.border}`, width: '100%' }}>
          <Toolbar sx={{ gap: 1, minHeight: { xs: 56, sm: 64 }, flexWrap: 'nowrap', width: '100%', maxWidth: '100%' }}>
            <IconButton onClick={handleDrawerToggle} sx={{ display: { md: 'none' }, flexShrink: 0 }}><MenuIcon /></IconButton>

            {/* Global Search */}
            <TextField
              size="small"
              placeholder={t('common.search')}
              InputProps={{
                startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: c.textSecondary }} /></InputAdornment>,
              }}
              sx={{ flex: '1 1 auto', minWidth: 0, maxWidth: { xs: 'none', sm: 400 }, '& .MuiOutlinedInput-root': { bgcolor: c.bgPage } }}
            />

            {/* Theme mode toggle + Language + user: stay at inline-end without a flex spacer (works in RTL) */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0, marginInlineStart: 'auto' }}>
            <IconButton
              onClick={toggleThemeMode}
              title={mode === 'dark' ? t('nav.switchToLight') : t('nav.switchToDark')}
              aria-label={mode === 'dark' ? t('nav.switchToLight') : t('nav.switchToDark')}
            >
              {mode === 'dark' ? <LightModeIcon /> : <DarkModeIcon />}
            </IconButton>
            <IconButton onClick={(e) => setLangAnchor(e.currentTarget)} title={t('nav.language')}>
              <LanguageIcon />
            </IconButton>
            <Menu anchorEl={langAnchor} open={!!langAnchor} onClose={() => setLangAnchor(null)}>
              {LANGUAGES.map(({ code, label }) => (
                <MenuItem key={code} onClick={() => handleLangSelect(code)}>{label}</MenuItem>
              ))}
            </Menu>

            {/* User */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Avatar sx={{ bgcolor: c.primary, width: 36, height: 36, fontSize: 14 }}>
                {user?.email?.[0]?.toUpperCase() || 'A'}
              </Avatar>
              <Typography variant="body2" fontWeight={600} sx={{ display: { xs: 'none', sm: 'block' } }}>
                {user?.email?.split('@')[0] || 'Admin'}
              </Typography>
            </Box>
            </Box>
          </Toolbar>
        </AppBar>

        {/* Page Content — wrapped in PageTransition for a subtle fade/slide on navigation. */}
        <Box component="main" sx={{ flex: 1, p: { xs: 2, sm: 3 }, overflow: 'auto' }}>
          <PageTransition>{children}</PageTransition>
        </Box>
      </Box>

      {/* Change Password Dialog */}
      <Dialog open={changePasswordOpen} onClose={() => !changePasswordLoading && setChangePasswordOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('changePassword.title')}</DialogTitle>
        <form onSubmit={handleChangePasswordSubmit}>
          <DialogContent>
            {changePasswordError && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setChangePasswordError(null)}>{changePasswordError}</Alert>}
            {changePasswordSuccess && <Alert severity="success" sx={{ mb: 2 }}>{t('changePassword.passwordUpdated')}</Alert>}
            <TextField label={t('changePassword.currentPassword')} type="password" fullWidth required value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} sx={{ mb: 2 }} />
            <TextField label={t('changePassword.newPassword')} type="password" fullWidth required value={newPassword} onChange={(e) => setNewPassword(e.target.value)} helperText={t('common.atLeast8Chars')} sx={{ mb: 2 }} />
            <TextField label={t('changePassword.confirmPassword')} type="password" fullWidth required value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setChangePasswordOpen(false)} disabled={changePasswordLoading}>{t('common.cancel')}</Button>
            <Button type="submit" variant="contained" disabled={changePasswordLoading || changePasswordSuccess}>
              {changePasswordLoading ? <CircularProgress size={20} /> : t('changePassword.updatePassword')}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
}
